import http from './http';
import AppConfig from '../AppConfig';
import { UserProfileResponse } from '../types/UserProfile';

/**
 * Fetches user profile data from the Compliance Education API
 * Note: Authentication is handled automatically by the http utility (Authorization header with ID token)
 */
export async function fetchUserProfile(): Promise<UserProfileResponse> {
  const url = `${AppConfig.complianceEducationApiUrl}/user/profile`;
  
  const response = await http.get(url);
  
  if (!response.ok) {
    let errorText: string;
    try {
      // Try to get error text from response
      errorText = await response.text();
    } catch (e) {
      // If response.text() fails, fall back to status text or generic message
      errorText = response.statusText || `HTTP ${response.status}`;
    }
    throw new Error(`Failed to fetch user profile: ${response.status} ${errorText}`);
  }
  
  return await response.json();
}


/**
 * One feature toggle for the signed-in user in the current org (CEE-1391).
 * The API scopes every call to the token's user and org.
 */
export interface UserFeature {
  featureKey: string;
  category: string;
  enabled: boolean;
  updatedAt: string | null;
}

async function readErrorText(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch (e) {
    return response.statusText || `HTTP ${response.status}`;
  }
}

export async function fetchUserFeatures(category: string): Promise<UserFeature[]> {
  const url = `${AppConfig.complianceEducationApiUrl}/user/features?category=${encodeURIComponent(category)}`;

  const response = await http.get(url);

  if (!response.ok) {
    const errorText = await readErrorText(response);
    throw new Error(`Failed to fetch user features: ${response.status} ${errorText}`);
  }

  const body = (await response.json()) as { data?: { features?: UserFeature[] } };
  return body.data?.features ?? [];
}

export async function updateUserFeature(
  featureKey: string,
  enabled: boolean,
): Promise<UserFeature> {
  const url = `${AppConfig.complianceEducationApiUrl}/user/features`;

  const response = await http.put(url, JSON.stringify({ featureKey, enabled }));

  if (!response.ok) {
    const errorText = await readErrorText(response);
    throw new Error(`Failed to update user feature: ${response.status} ${errorText}`);
  }

  const body = (await response.json()) as { data: UserFeature };
  return body.data;
}
